import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  executeSophiaAction,
  selectSelectedProducts,
  selectThreadAppState,
} from '../thread/threadSlice';
import { buildImage, NO_IMAGE_PLACEHOLDER } from '../../utils/product';
import { getTagColor } from '../../utils/tagRendering';
import DetailsModal from '../../components/DetailsModal';

interface CatalogSchemaEntry {
  dtype: string;
  enum?: string[];
  [key: string]: unknown;
}

interface CatalogPageMeta {
  total: number;
  page: number;
  page_size: number;
  pages: number;
  [key: string]: unknown;
}

interface CatalogPageResponse {
  data?: Record<string, unknown>[];
  meta?: CatalogPageMeta | Record<string, unknown>;
  schema?: Record<string, CatalogSchemaEntry> | Record<string, unknown>;
  [key: string]: unknown;
}

interface SophiaListProductsResponse {
  'global-page-data'?: {
    products?: CatalogPageResponse;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface SortRow {
  id: number;
  column: string;
  direction: 'asc (+)' | 'desc (-)';
}

interface FilterRow {
  id: number;
  column: string;
  operator: string;
  value: string;
}

const FILTER_OPERATORS = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'contains', 'in'] as const;
const DEFAULT_PAGE_SIZE = 25;

// SearchableSelect Component
interface SearchableSelectProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

function SearchableSelect({ options, value, onChange, placeholder = 'Cerca...', className = '' }: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lower = searchTerm.toLowerCase();
    return options.filter(opt => 
      opt.label.toLowerCase().includes(lower) || 
      opt.value.toLowerCase().includes(lower)
    );
  }, [options, searchTerm]);

  const selectedLabel = useMemo(() => {
    const found = options.find(opt => opt.value === value);
    return found ? found.label : value;
  }, [options, value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 px-2 py-[0.47rem] text-left bg-white flex items-center justify-between appearance-none text-sm"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: 'right 0.5rem center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '1.5em 1.5em',
          paddingRight: '2.5rem',
        }}
      >
        <span className="truncate">{selectedLabel}</span>
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-64 flex flex-col">
          <div className="p-2 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={placeholder}
              className="w-full px-2 py-1 border border-gray-300 text-sm"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">Nessun risultato</div>
            ) : (
              filteredOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                    option.value === value ? 'bg-gray-200 font-semibold' : ''
                  }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Get appropriate filter operators based on column data type
function getOperatorsForColumn(dtype: string): Array<{ value: string; label: string }> {
  const normalizedDtype = dtype.toLowerCase();
  
  // Numeric types
  if (normalizedDtype.includes('int') || normalizedDtype.includes('float') || normalizedDtype.includes('number')) {
    return [
      { value: 'eq', label: 'Uguale a' },
      { value: 'ne', label: 'Diverso da' },
      { value: 'lt', label: 'Minore di' },
      { value: 'lte', label: '≤' },
      { value: 'gt', label: 'Maggiore di' },
      { value: 'gte', label: '≥' },
      { value: 'in', label: 'È uno di' },
    ];
  }
  
  // Boolean types
  if (normalizedDtype.includes('bool')) {
    return [
      { value: 'eq', label: 'Uguale a' },
      { value: 'ne', label: 'Diverso da' },
    ];
  }
  
  // Date types
  if (normalizedDtype.includes('date') || normalizedDtype.includes('time')) {
    return [
      { value: 'eq', label: 'Uguale a' },
      { value: 'ne', label: 'Diverso da' },
      { value: 'lt', label: 'Prima di' },
      { value: 'lte', label: 'Prima o uguale a' },
      { value: 'gt', label: 'Dopo' },
      { value: 'gte', label: 'Dopo o uguale a' },
    ];
  }
  
  // String types (default)
  return [
    { value: 'contains', label: 'Contiene' },
    { value: 'eq', label: 'Uguale a' },
    { value: 'ne', label: 'Diverso da' },
    { value: 'in', label: 'È uno di' },
  ];
}

export const MAPPING_COLUMNS = {
  "cod_stagione": "stagione",
  "cod_modello": "modello",
  "cod_colore": "colore",
  "short_descr": "descrizione breve",
  "long_descr": "descrizione lunga",
  "best_seller": "best seller",
  "bgadescriz1": "bga descrizione 1",
  "cod_modello_origine": "modello origine",
  "prezzo": "prezzo",
  "attr_modellario": "attributi modellario",
  "sku": "sku",
  "gender": "linea",
  "macro_categoria": "macro categoria",
  "micro_categoria": "micro categoria",
  "categoria": "categoria",
  "tipo_filato": "tipo filato",
  "consegna": "consegna",
  "fit": "fit",
  "fascia_prezzo": "fascia prezzo",
  "neck": "tipo di collo",
  "print": "presenza stampa",
  "sleeved": "tipo di manica",
  "jewel": "presenza monile",
  "pocket": "presenza tasca",
  "stripes": "presenza strisce",
  "woman_shoes": "tipo di scarpe",
  "jacket_closure": "tipo di chiusura giacca",
  "first_url_image": "url immagine principale",
  "sketch_url": "url sketch",
  "url_img_list": "lista url immagini",
  "master_order_char": "master order",
  "cod_immagine": "codice immagine",
  "weborder_tags": "weborder tags",
  "tag_weborder": "weborder tags",
};

// Mapping per trasformare i valori visualizzati
export const formatColumnValue = (column: string, value: unknown): string => {
  const stringValue = String(value);
  const lowerColumn = column.toLowerCase();
  
  switch (lowerColumn) {
    case 'print':
      return stringValue === 'print' ? 'si' : 'no';
    case 'jewel':
      return stringValue === 'jewel' ? 'si' : 'no';
    case 'pocket':
      return stringValue === 'pocket' ? 'si' : 'no';
    case 'stripes':
      return stringValue === 'striped' ? 'si' : 'no';
    case 'master_order_char':
      return stringValue === 'M' ? 'si' : 'no';
    default:
      return stringValue;
  }
};

function normaliseSchema(value: unknown): Record<string, CatalogSchemaEntry> {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const entries = value as Record<string, unknown>;
  const schema: Record<string, CatalogSchemaEntry> = {};
  Object.entries(entries).forEach(([key, entry]) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const record = entry as Record<string, unknown>;
    const dtype = typeof record.dtype === 'string' ? record.dtype : 'string';
    const enumValues = Array.isArray(record.enum)
      ? (record.enum as unknown[]).filter((item): item is string => typeof item === 'string')
      : undefined;
    schema[key] = {
      ...record,
      dtype,
      ...(enumValues && enumValues.length ? { enum: enumValues } : {}),
    } as CatalogSchemaEntry;
  });
  return schema;
}

function normaliseMeta(value: unknown): CatalogPageMeta | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  const total = Number(record.total ?? 0);
  const page = Number(record.page ?? 1);
  const pageSize = Number(record.page_size ?? DEFAULT_PAGE_SIZE);
  const pages = Number(record.pages ?? 1);
  const meta: CatalogPageMeta = {
    ...record,
    total: Number.isFinite(total) ? total : 0,
    page: Number.isFinite(page) ? page : 1,
    page_size: Number.isFinite(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    pages: Number.isFinite(pages) ? pages : 1,
  };
  return meta;
}

function parseSortTokens(value: unknown): SortRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  let nextId = 1;
  return (value as unknown[])
    .map(token => {
      if (typeof token !== 'string' || token.length < 2) {
        return null;
      }
      const direction: SortRow['direction'] = token.startsWith('-') ? 'desc (-)' : 'asc (+)';
      const column = token.slice(1);
      if (!column) {
        return null;
      }
      const row: SortRow = {
        id: nextId,
        column,
        direction,
      };
      nextId += 1;
      return row;
    })
    .filter((row): row is SortRow => Boolean(row));
}

function parseFilterTokens(value: unknown): FilterRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  let nextId = 1;
  return (value as unknown[])
    .map(token => {
      if (typeof token !== 'string') {
        return null;
      }
      const [column, operator, ...rest] = token.split('|');
      if (!column) {
        return null;
      }
      const valuePart = rest.join('|');
      const row: FilterRow = {
        id: nextId,
        column,
        operator: operator ?? 'eq',
        value: valuePart,
      };
      nextId += 1;
      return row;
    })
    .filter((row): row is FilterRow => Boolean(row));
}

function ensureStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return (value as unknown[]).filter((item): item is string => typeof item === 'string');
}

export function ListProducts() {
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [schema, setSchema] = useState<Record<string, CatalogSchemaEntry>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tempSearchTerm, setTempSearchTerm] = useState<string>('');
  const [sortRows, setSortRows] = useState<SortRow[]>([]);
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<CatalogPageMeta | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [queryLoading, setQueryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queryVersion, setQueryVersion] = useState(0);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [expandedLongDescr, setExpandedLongDescr] = useState<Set<string>>(new Set());
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  const dispatch = useAppDispatch();
  const selectedSkus = useAppSelector(selectSelectedProducts);
  const appState = useAppSelector(selectThreadAppState);
  const threadStatus = useAppSelector(state => state.thread.status);

  const allColumns = useMemo(() => 
    Object.keys(schema).filter(column => 
      !['SKETCH_URL', 'URL_IMG_LIST', 'COD_IMMAGINE'].includes(column)
    ), [schema]);

  // Memoize row selection state to ensure proper re-renders
  const rowsWithSelection = useMemo(() => {
    const allRows = rows.map((row, index) => {
      const sku = String(row['SKU']);
      const isSelected = selectedSkus.includes(sku);
      return { 
        rowData: row,
        sku, 
        isSelected, 
        originalIndex: index 
      };
    });
    
    return allRows;
  }, [rows, selectedSkus, showOnlySelected]);

  useEffect(() => {
    // Handle simple search
    const remoteSearch = appState?.['list-products-search'];
    
    if (typeof remoteSearch === 'string') {
      setSearchTerm(remoteSearch);
      setTempSearchTerm(remoteSearch);
    } else {
      setSearchTerm('');
      setTempSearchTerm('');
    }

    const remoteSort = appState?.['list-products-sort'];
    setSortRows(parseSortTokens(remoteSort));

    const remoteFilters = appState?.['list-products-filter'];
    setFilters(parseFilterTokens(remoteFilters));

    const remoteColumns = appState?.['list-products-columns'];
    const columnValues = ensureStringArray(remoteColumns);
    // Filter out image columns and SKU from remote state
    const filteredColumns = columnValues.filter(column => 
      !['SKETCH_URL', 'URL_IMG_LIST', 'COD_IMMAGINE', 'SKU'].includes(column)
    );
    // Always update selectedColumns, even if empty (this handles "deselect all" case)
    setSelectedColumns(filteredColumns);

    const remotePage = appState?.['list-products-page'];
    if (typeof remotePage === 'number') {
      setPage(remotePage);
    } else if (typeof remotePage === 'string') {
      const parsed = Number(remotePage);
      if (!Number.isNaN(parsed)) {
        setPage(parsed);
      }
    }

    // Sync showOnlySelected state
    const onlySelected = appState?.['list-products-show-only-selected'];
    if (typeof onlySelected === 'boolean') {
      setShowOnlySelected(onlySelected);
    }

  }, [appState]);


  

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (threadStatus !== 'succeeded') {
      return;
    }
    
    async function loadPage() {
      try {
        setQueryLoading(true);
        
        const payload = await dispatch(
          executeSophiaAction({
            actionName: 'global-get-page-data',
            params: { 'global-page-name': 'list-products' },
            refreshThread: false,
          }),
        ).unwrap();
        const response = payload.data as SophiaListProductsResponse;
        const products = response?.['global-page-data']?.products;
        const nextSchema = normaliseSchema(response?.['global-page-data']?.schema);
        
        setSchema(nextSchema);
        // Preserve user's column selection
        // Only auto-populate columns if selectedColumns was never set (undefined in appState)
        setSelectedColumns(current => {
          // If user has made a selection (including deselecting all), keep it
          if (appState?.['list-products-columns'] !== undefined) {
            return current;
          }
          // First load: populate with all available columns
          if (current.length === 0) {
            return Object.keys(nextSchema).filter(column => 
              !['SKETCH_URL', 'URL_IMG_LIST', 'COD_IMMAGINE', 'SKU'].includes(column)
            );
          }
          // Otherwise keep current selection
          return current;
        });
        
        const metaData = normaliseMeta(products?.meta);
        setMeta(metaData);
        
        if (metaData) {
          setPage(metaData.page);
        }
        
        const dataRows = Array.isArray(products?.data)
          ? (products?.data as Record<string, unknown>[])
          : [];
        setRows(dataRows);
        setError(null);

        // Apply default sort only on first load and if no sort is already set
        if (isInitialLoad && (!appState?.['list-products-sort'] || (Array.isArray(appState['list-products-sort']) && appState['list-products-sort'].length === 0))) {
          await applyQueryWithSearch('', true);
          setIsInitialLoad(false);
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setQueryLoading(false);
      }
    }

    void loadPage();
  }, [dispatch, threadStatus, queryVersion]);

  const runListProductsAction = useCallback(
    async (actionName: string, params?: Record<string, unknown>) => {
      await dispatch(
        executeSophiaAction({
          actionName,
          params,
          refreshThread: true,
        }),
      ).unwrap();
    },
    [dispatch],
  );

  const toggleSortRow = useCallback(() => {
    setSortRows(current => {
      const nextId = current.length ? Math.max(...current.map(row => row.id)) + 1 : 1;
      return [...current, { id: nextId, column: '(seleziona)', direction: 'asc (+)' }];
    });
  }, []);

  const removeSortRow = useCallback((id: number) => {
    setSortRows(current => current.filter(row => row.id !== id));
  }, []);

  const addFilterRow = useCallback(() => {
    setFilters(current => {
      const nextId = current.length ? Math.max(...current.map(row => row.id)) + 1 : 1;
      return [...current, { id: nextId, column: '(seleziona)', operator: 'eq', value: '' }];
    });
  }, []);

  const removeFilterRow = useCallback((id: number) => {
    setFilters(current => current.filter(row => row.id !== id));
  }, []);



  const applyQueryWithSearch = useCallback(async (searchToUse: string, isInitialLoad = false) => {
    setQueryLoading(true);
    try {
      const sortTokens = sortRows
        .map(row => {
          if (!row.column || row.column === '(seleziona)') {
            return null;
          }
          return `${row.direction.startsWith('asc') ? '+' : '-'}${row.column}`;
        })
        .filter((token): token is string => Boolean(token));

      // Only add default -SKU sort on initial load, not when user manually applies query
      let finalSortTokens = sortTokens;
      if (isInitialLoad && sortTokens.length === 0) {
        finalSortTokens = ['-SKU'];
      }

      const filterTokens = filters
        .map(row => {
          if (!row.column || row.column === '(seleziona)') {
            return null;
          }
          return `${row.column}|${row.operator}|${row.value ?? ''}`;
        })
        .filter((token): token is string => Boolean(token));

      const nextPage = 1;
      setPage(nextPage);
      setError(null);

      try {
        // Use simple global search (searches across all text columns)
        if (searchToUse && searchToUse.trim()) {
          await runListProductsAction('list-products-set-search', {
            'list-products-search': searchToUse.trim(),
          });
        } else {
          await runListProductsAction('list-products-unset-search');
        }

        // Apply sort - only includes default SKU on initial load
        if (finalSortTokens.length > 0) {
          await runListProductsAction('list-products-set-sort', {
            'list-products-sort': finalSortTokens,
          });
        } else {
          await runListProductsAction('list-products-unset-sort');
        }

        if (filterTokens.length) {
          await runListProductsAction('list-products-set-filter', {
            'list-products-filter': filterTokens,
          });
        } else {
          await runListProductsAction('list-products-unset-filter');
        }

        // Always ensure SKU and image fields are included
        // When selectedColumns is empty, only essential columns (SKU and images) are shown
        const columnsWithEssentials = [...new Set([
          ...selectedColumns,
          'SKU',           // Always include SKU for product operations
          'SKETCH_URL',
          'URL_IMG_LIST',
          'COD_IMMAGINE'
        ])];
        
        await runListProductsAction('list-products-set-columns', {
          'list-products-columns': columnsWithEssentials,
        });

        await runListProductsAction('list-products-set-page', {
          'list-products-page': nextPage,
        });
      } catch (err) {
        setError((err as Error).message);
        return;
      }

      setQueryVersion(version => version + 1);
    } finally {
      setQueryLoading(false);
    }
  }, [filters, runListProductsAction, selectedColumns, sortRows]);

  const applyQuery = useCallback(async () => {
    await applyQueryWithSearch(searchTerm, false);
  }, [applyQueryWithSearch, searchTerm]);

  const changePage = useCallback(
    async (nextPage: number) => {
      setQueryLoading(true);
      try {
        const target = Math.max(1, nextPage);
        setError(null);
        try {
          await runListProductsAction('list-products-set-page', {
            'list-products-page': target,
          });
          setPage(target);
          setQueryVersion(version => version + 1);
        } catch (err) {
          setError((err as Error).message);
        }
      } finally {
        setQueryLoading(false);
      }
    },
    [runListProductsAction],
  );
  // add new action if show only selected is true
  const handleShowOnlySelected = useCallback(async () => {
    setQueryLoading(true);
    try {
      await runListProductsAction('list-products-set-show-only-selected', {
        'list-products-show-only-selected': true,
      });
      setQueryVersion(version => version + 1);
    } finally {
      setQueryLoading(false);
    }
  }, [runListProductsAction]);

  const handleHideOnlySelected = useCallback(async () => {
    setQueryLoading(true);
    try {
      await runListProductsAction('list-products-set-show-only-selected', {
        'list-products-show-only-selected': false,
      });
      setQueryVersion(version => version + 1);
    } finally {
      setQueryLoading(false);
    }
  }, [runListProductsAction]);

  const handlePrev = useCallback(() => {
    const target = Math.max(1, page - 1);
    if (target === page) {
      return;
    }
    void changePage(target);
  }, [changePage, page]);

  const handleNext = useCallback(() => {
    const totalPages = meta?.pages ?? page;
    const target = Math.min(totalPages, page + 1);
    if (target === page) {
      return;
    }
    void changePage(target);
  }, [changePage, meta?.pages, page]);

  const handleToggleSelection = useCallback(
    async (sku: string, selected: boolean) => {
      await dispatch(
        executeSophiaAction({
          actionName: selected ? 'global-unset-select-product' : 'global-set-select-product',
          params: { 'global-select-product': sku },
          refreshThread: true,
        }),
      );
    },
    [dispatch],
  );

  const handleShowDetails = useCallback(
    (sku: string) => {
      setSelectedSku(sku);
      setDetailsModalOpen(true);
    },
    [],
  );

  const handleCloseDetailsModal = useCallback(() => {
    setDetailsModalOpen(false);
    setSelectedSku(null);
  }, []);

  const handleSkuChange = useCallback((newSku: string) => {
    setSelectedSku(newSku);
    setDetailsModalOpen(true);
  }, []);

  const toggleLongDescr = useCallback((sku: string) => {
    setExpandedLongDescr(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  }, []);

  // Handle ESC key and click outside for modals
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (sortModalOpen) setSortModalOpen(false);
        if (filterModalOpen) setFilterModalOpen(false);
        if (columnsModalOpen) setColumnsModalOpen(false);
        if (searchModalOpen) setSearchModalOpen(false);
      }
    };

    if (sortModalOpen || filterModalOpen || columnsModalOpen || searchModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [sortModalOpen, filterModalOpen, columnsModalOpen, searchModalOpen]);

  return (
    <div className="w-full h-full pl-6 flex flex-col">
      {/* Controls bar and pagination */}
      <div className="w-full flex flex-row items-center justify-between mb-6 mt-2 sticky top-4 bg-white z-20 py-3 border-b border-gray-100 flex-shrink-0">
        <div className="flex gap-3 items-center">
          <button
            type="button"
            onClick={() => setFilterModalOpen(true)}
            className="bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
          >
            FILTRI{filters.filter(f => f.column && f.column !== '(pick)' && f.value.trim()).length > 0 ? ` (${filters.filter(f => f.column && f.column !== '(pick)' && f.value.trim()).length})` : ''}
          </button>
          <button
            type="button"
            onClick={() => setColumnsModalOpen(true)}
            className="bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
          >
            COLONNE ({selectedColumns.length})
          </button>
          <button
            type="button"
            onClick={() => setSortModalOpen(true)}
            className="bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
          >
            ORDINAMENTO{sortRows.filter(row => row.column && row.column !== '(seleziona)').length > 0 ? ` (${sortRows.filter(row => row.column && row.column !== '(seleziona)').length})` : ''}
          </button>
          <button
            type="button"
            onClick={() => {
              setTempSearchTerm(searchTerm);
              setSearchModalOpen(true);
            }}
            className="bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
          >
            CERCA{searchTerm ? ' (1)' : ''}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowOnlySelected(!showOnlySelected);
              if (!showOnlySelected) {
                void handleShowOnlySelected();
              } else {
                void handleHideOnlySelected();
              }
            }}
            className={`px-4 py-2 text-sm font-semibold shadow-sm ${
              showOnlySelected 
                ? 'bg-black text-white hover:bg-gray-800' 
                : 'bg-white text-black border-2 border-black hover:bg-gray-100'
            }`}
          >
            {showOnlySelected ? '✓ SOLO SELEZIONATI' : 'SOLO SELEZIONATI'}
          </button>
        </div>
        <div className="flex flex-row items-center gap-3 pr-8">
          <button
            type="button"
            onClick={handlePrev}
            className="text-black hover:text-gray-600 px-2 py-1 text-xl"
          >
            ◀
          </button>
          <p className="text-sm text-gray-600">
            {meta
              ? `Pagina ${meta.page} di ${meta.pages}`
              : 'No data loaded'}
          </p>
          <button
            type="button"
            onClick={handleNext}
            className="text-black hover:text-gray-600 px-2 py-1 text-xl"
          >
            ▶
          </button>
        </div>
      </div>

      {/* Sort Modal */}
      {sortModalOpen && (
        <div 
          className="fixed inset-0 overlay-in z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setSortModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-96 max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Ordina</h2>
              <button
                type="button"
                onClick={() => setSortModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col gap-3 mb-4">
              {sortRows.map(row => (
                <div key={row.id} className="flex w-full items-end gap-3">
                  <SearchableSelect
                    className="w-1/2"
                    options={[
                      { value: '(seleziona)', label: '(seleziona)' },
                      ...allColumns.map(column => ({
                        value: column,
                        label: MAPPING_COLUMNS[column.toLowerCase() as keyof typeof MAPPING_COLUMNS] || column
                      }))
                    ]}
                    value={row.column}
                    onChange={(value) =>
                      setSortRows(current =>
                        current.map(item =>
                          item.id === row.id
                            ? { ...item, column: value }
                            : item,
                        ),
                      )
                    }
                    placeholder="Cerca colonna..."
                  />
                  <select
                    className="w-1/3 border border-gray-300 px-2 py-2"
                    value={row.direction}
                    onChange={event =>
                      setSortRows(current =>
                        current.map(item =>
                          item.id === row.id
                            ? {
                                ...item,
                                direction: event.target.value as SortRow['direction'],
                              }
                            : item,
                        ),
                      )
                    }
                  >
                    <option value="asc (+)">asc (+)</option>
                    <option value="desc (-)">desc (-)</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeSortRow(row.id)}
                    className="bg-white border border-gray-300 px-3 py-2 text-sm font-semibold text-black hover:bg-gray-100"
                  >
                    −
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={toggleSortRow}
                className="w-fit bg-white border border-gray-300 px-3 py-2 text-sm font-semibold text-black hover:bg-gray-100"
              >
                + aggiungi ord.
              </button>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setSortModalOpen(false)}
                className="bg-white border border-gray-300 px-4 py-2 text-sm font-semibold text-black hover:bg-gray-100"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={() => {
                  void applyQuery();
                  setSortModalOpen(false);
                }}
                className="bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
              >
                Applica
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {filterModalOpen && (
        <div 
          className="fixed inset-0 overlay-in z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setFilterModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-[500px] max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Filtri</h2>
              <button
                type="button"
                onClick={() => setFilterModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col gap-3 mb-4">
              {filters.map(row => {
                // Check if the selected column has enum values
                const columnSchema = schema[row.column];
                const hasEnum = columnSchema?.enum && Array.isArray(columnSchema.enum) && columnSchema.enum.length > 0;
                
                // Get appropriate operators for the column's data type
                const dtype = columnSchema?.dtype || 'string';
                const availableOperators = getOperatorsForColumn(dtype);
                
                // Reset operator if current one is not available for this dtype
                const currentOperatorValid = availableOperators.some(op => op.value === row.operator);
                
                return (
                  <div key={row.id} className="flex w-full items-end gap-3">
                    <SearchableSelect
                      className="w-2/5"
                      options={[
                        { value: '(seleziona)', label: '(seleziona)' },
                        ...allColumns.map(column => ({
                          value: column,
                          label: MAPPING_COLUMNS[column.toLowerCase() as keyof typeof MAPPING_COLUMNS] || column
                        }))
                      ]}
                      value={row.column}
                      onChange={(newColumn) => {
                        const newColumnSchema = schema[newColumn];
                        const newDtype = newColumnSchema?.dtype || 'string';
                        const newOperators = getOperatorsForColumn(newDtype);
                        
                        setFilters(current =>
                          current.map(item =>
                            item.id === row.id
                              ? { 
                                  ...item, 
                                  column: newColumn, 
                                  value: '', // Reset value when column changes
                                  operator: newOperators[0]?.value || 'eq' // Reset to first available operator
                                }
                              : item,
                          ),
                        );
                      }}
                      placeholder="Cerca colonna..."
                    />
                    <select
                      className="w-1/4 border border-gray-300 px-2 py-2"
                      value={currentOperatorValid ? row.operator : availableOperators[0]?.value || 'eq'}
                      onChange={event =>
                        setFilters(current =>
                          current.map(item =>
                            item.id === row.id
                              ? { ...item, operator: event.target.value }
                              : item,
                          ),
                        )
                      }
                    >
                      {availableOperators.map(op => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                    
                    {/* Conditional rendering: dropdown for enum columns, input for others */}
                    {hasEnum ? (
                      <SearchableSelect
                        className="w-2/5"
                        options={[
                          { value: '', label: '(seleziona valore)' },
                          ...(columnSchema.enum?.map(enumValue => ({
                            value: enumValue,
                            label: enumValue
                          })) || [])
                        ]}
                        value={row.value}
                        onChange={(value) =>
                          setFilters(current =>
                            current.map(item =>
                              item.id === row.id
                                ? { ...item, value }
                                : item,
                            ),
                          )
                        }
                        placeholder="Cerca valore..."
                      />
                    ) : (
                      <input
                        className="w-2/5 border border-gray-300 px-2 py-2 h-[2.32rem]"
                        value={row.value}
                        onChange={event =>
                          setFilters(current =>
                            current.map(item =>
                              item.id === row.id
                                ? { ...item, value: event.target.value }
                                : item,
                            ),
                          )
                        }
                        placeholder={row.column === '(seleziona)' ? 'Seleziona prima una colonna' : 'Inserisci valore...'}
                        disabled={row.column === '(seleziona)'}
                      />
                    )}
                    
                    <button
                      type="button"
                      onClick={() => removeFilterRow(row.id)}
                      className="bg-white border border-gray-300 px-3 py-2 text-sm font-semibold text-black hover:bg-gray-100"
                    >
                      −
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={addFilterRow}
                className="w-fit bg-white border border-gray-300 px-3 py-2 text-sm font-semibold text-black hover:bg-gray-100"
              >
                + aggiungi filtro
              </button>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setFilterModalOpen(false)}
                className="bg-white border border-gray-300 px-4 py-2 text-sm font-semibold text-black hover:bg-gray-100"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={() => {
                  void applyQuery();
                  setFilterModalOpen(false);
                }}
                className="bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
              >
                Applica
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Columns Modal */}
      {columnsModalOpen && (
        <div 
          className="fixed inset-0 overlay-in z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setColumnsModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-[600px] max-w-full h-[60vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">Colonne</h2>
              <button
                type="button"
                onClick={() => setColumnsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="mb-4 flex-1 overflow-y-auto">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Seleziona colonne da aggiungere:
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedColumns([...allColumns])}
                    className="bg-black px-3 py-1 text-xs font-semibold text-white hover:bg-gray-800"
                  >
                    Seleziona tutto
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedColumns([])}
                    className="bg-white border border-gray-300 px-3 py-1 text-xs font-semibold text-black hover:bg-gray-100"
                  >
                    Deseleziona tutto
                  </button>
                </div>
              </div>
              <SearchableSelect
                className="w-full"
                options={[
                  { value: '', label: '-- Seleziona una colonna --' },
                  ...allColumns
                    .filter(column => !selectedColumns.includes(column))
                    .map(column => ({
                      value: column,
                      label: MAPPING_COLUMNS[column.toLowerCase() as keyof typeof MAPPING_COLUMNS] || column
                    }))
                ]}
                value=""
                onChange={(columnToAdd) => {
                  if (columnToAdd && !selectedColumns.includes(columnToAdd)) {
                    setSelectedColumns([...selectedColumns, columnToAdd]);
                  }
                }}
                placeholder="Cerca colonna..."
              />
              
              {/* Selected columns as tags */}
              {selectedColumns.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Colonne selezionate:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedColumns.map(column => (
                      <div
                        key={column}
                        className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm"
                      >
                        <span>{MAPPING_COLUMNS[column.toLowerCase() as keyof typeof MAPPING_COLUMNS] || column}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedColumns(selectedColumns.filter(col => col !== column));
                          }}
                          className="text-gray-500 hover:text-gray-700 ml-1"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setColumnsModalOpen(false)}
                className="bg-white border border-gray-300 px-4 py-2 text-sm font-semibold text-black hover:bg-gray-100"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={() => {
                  void applyQuery();
                  setColumnsModalOpen(false);
                }}
                className="bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
              >
                Applica
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simple Search Modal */}
      {searchModalOpen && (
        <div 
          className="fixed inset-0 overlay-in z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setSearchModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-[400px] max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Cerca Prodotti</h2>
              <button
                type="button"
                onClick={() => setSearchModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Termine di ricerca
              </label>
              <input
                type="text"
                placeholder="Inserisci il termine da cercare..."
                value={tempSearchTerm}
                onChange={event => setTempSearchTerm(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 text-sm shadow-sm focus:border-black focus:outline-none"
                autoFocus
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setTempSearchTerm(searchTerm);
                  setSearchModalOpen(false);
                }}
                className="bg-white border border-gray-300 px-4 py-2 text-sm font-semibold text-black hover:bg-gray-100"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={async () => {
                  setSearchTerm(tempSearchTerm);
                  setSearchModalOpen(false);
                  
                  // Apply search immediately with new values
                  await applyQueryWithSearch(tempSearchTerm, false);
                }}
                className="bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
              >
                Cerca
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={async () => {
                    setSearchTerm('');
                    setTempSearchTerm('');
                    setSearchModalOpen(false);
                    // Remove search
                    await runListProductsAction('list-products-unset-search');
                    setQueryVersion(version => version + 1);
                  }}
                  className="bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
                >
                  Rimuovi
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table Container*/}
      <div className="flex-1 w-full border border-gray-200 overflow-auto relative">
        {/* Query Loading Spinner Overlay */}
        {queryLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin-in rounded-full h-12 w-12 border-b-2 border-black"></div>
              <p className="text-sm font-semibold text-gray-700">Caricamento...</p>
            </div>
          </div>
        )}
        
        {error && <p className="p-4 text-sm text-red-500">{error}</p>}
        {!error && (
          <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
            <thead className="bg-[#E5E5E5] sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ width: '140px', minWidth: '140px' }}>
                  Select
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ width: '150px', minWidth: '150px' }}>
                  SKU
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ width: '120px', minWidth: '120px' }}>
                  Image
                </th>
                {selectedColumns.map(column => (
                  <th
                    key={column}
                    className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    style={column === 'LONG_DESCR' ? { width: '500px', minWidth: '500px' } : {}}
                  >
                    {MAPPING_COLUMNS[column.toLowerCase() as keyof typeof MAPPING_COLUMNS] || column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rowsWithSelection.map(({ rowData, sku, isSelected, originalIndex }) => {
                return (
                  <tr key={`${sku}-${originalIndex}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3" style={{ width: '140px', minWidth: '140px' }}>
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                void handleToggleSelection(sku, isSelected);
                                }}
                                className={`px-3 py-2 text-xs font-semibold flex items-center justify-center gap-x-2 w-full min-w-[140px] h-10 ${
                                isSelected ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'
                                }`}
                            >
                                <span>{isSelected ? 'SELEZIONATO' : 'SELEZIONA'}</span>
                                <div className={`w-4 h-4 border-2 flex items-center justify-center ${
                                    isSelected 
                                        ? 'border-white bg-transparent' 
                                        : 'border-black bg-white'
                                }`}>
                                    {isSelected && (
                                        <span className="text-white text-xs leading-none">✓</span>
                                    )}
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleShowDetails(sku)}
                                className="px-3 py-2 text-xs font-semibold bg-white text-black border-2 border-black w-full min-w-[140px] h-10"
                            >
                                DETTAGLI
                            </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-medium" style={{ width: '150px', minWidth: '150px' }}>
                      {sku}
                    </td>
                    <td className="px-4 py-3" style={{ width: '120px', minWidth: '120px' }}>
                      {(() => {
                        // Data comes with uppercase keys
                        const urlImgList = (rowData as any)?.URL_IMG_LIST || (rowData as any)?.url_img_list;
                        const sketchUrl = (rowData as any)?.SKETCH_URL || (rowData as any)?.sketch_url;
                        
                        let imageUrl: string | null = null;
                        
                        // Handle URL_IMG_LIST as serialized string with {}
                        if (typeof urlImgList === 'string' && urlImgList.trim()) {
                          const cleanUrl = urlImgList.replace(/[{}]/g, '').split(',')[0]?.trim();
                          if (cleanUrl) {
                            imageUrl = buildImage(cleanUrl);
                          }
                        } else if (Array.isArray(urlImgList) && urlImgList.length > 0) {
                          imageUrl = buildImage(urlImgList[0] as string);
                        }
                        
                        // Fallback to SKETCH_URL
                        if (!imageUrl && typeof sketchUrl === 'string' && sketchUrl.trim()) {
                          imageUrl = buildImage(sketchUrl);
                        }
                        
                        return (
                          <img
                            src={imageUrl ?? NO_IMAGE_PLACEHOLDER}
                            alt={String(rowData['SKU'] ?? sku)}
                            className="h-[88px] w-[88px] object-cover rounded"
                            style={{ aspectRatio: '1/1' }}
                            loading="lazy"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              // If image fails and doesn't already have cache-busting, try with it
                              if (target.src === NO_IMAGE_PLACEHOLDER) {
                                return;
                              }
                              if (!target.src.includes('?v=')) {
                                const originalSrc = target.src.split('?')[0];
                                const cacheBustSrc = `${originalSrc}?v=${Date.now()}`;
                                target.src = cacheBustSrc;
                              } else {
                                target.src = NO_IMAGE_PLACEHOLDER;
                              }
                            }}
                          />
                        );
                      })()}
                    </td>
                    {selectedColumns.map(column => (
                      <td 
                        key={column} 
                        className="px-4 py-3 text-sm text-gray-700"
                        style={column === 'LONG_DESCR' ? { width: '500px', minWidth: '500px' } : {}}
                      >
                        {column === 'LONG_DESCR' ? (
                          <div 
                            className={`cursor-pointer hover:bg-gray-50 p-2 rounded ${
                              expandedLongDescr.has(sku) ? '' : 'overflow-hidden'
                            }`}
                            style={expandedLongDescr.has(sku) ? {} : {
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: '1.5',
                              maxHeight: '6em'
                            }}
                            onClick={() => toggleLongDescr(sku)}
                          >
                            {formatColumnValue(column, rowData[column] ?? '')}
                          </div>
                        ) : (column === 'WEBORDER_TAGS' || column === 'TAG_WEBORDER') ? (
                          (() => {
                            // Handle weborder tags with special rendering
                            const tags = rowData[column];
                            if (!tags) return '-';
                            
                            // Parse tags if they're in string format
                            let parsedTags: string[] = [];
                            
                            if (typeof tags === 'string') {
                              try {
                                // Try to parse as JSON array
                                const parsed = JSON.parse(tags);
                                if (Array.isArray(parsed)) {
                                  parsedTags = parsed;
                                }
                              } catch {
                                // If not JSON, split by comma for comma-separated strings
                                parsedTags = tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
                              }
                            } else if (Array.isArray(tags)) {
                              parsedTags = tags;
                            }

                            if (parsedTags.length === 0) return '-';

                            return (
                              <div className="flex flex-col gap-1 flex-wrap items-start">
                                {parsedTags.map((tag, index) => {
                                  const bgColor = getTagColor(tag);
                                  return (
                                    <span
                                      key={index}
                                      className="px-2 py-0.5 font-semibold text-white"
                                      style={{ 
                                        backgroundColor: bgColor,
                                        borderRadius: '8px',
                                        fontSize: '9px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        lineHeight: '1.2',
                                        opacity: 0.9
                                      }}
                                    >
                                      {tag}
                                    </span>
                                  );
                                })}
                              </div>
                            );
                          })()
                        ) : (
                          formatColumnValue(column, rowData[column] ?? '')
                        )}
                      </td>
                    ))}
                    
                    
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Details Modal */}
      <DetailsModal
        sku={selectedSku}
        isOpen={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        onSkuChange={handleSkuChange}
      />
    </div>
  );
}

export default ListProducts;
